export type AiChatFeedbackSentiment = "positive" | "negative"

export const AI_CHAT_FEEDBACK_ENRICHMENT_KIND = "ai_chat_feedback"

// Both widget clients compose these sentences, so they are the only handle on
// ratings written before the enrichment marker existed.
export const AI_CHAT_FEEDBACK_PREFIXES = [
	"The user liked this response",
	"The user disliked this response",
] as const

export function aiChatFeedbackEnrichments(
	sentiment: AiChatFeedbackSentiment,
	ratedMessageId?: string | null,
): {
	kind: string
	sentiment: AiChatFeedbackSentiment
	ratedMessageId?: string
} {
	return {
		kind: AI_CHAT_FEEDBACK_ENRICHMENT_KIND,
		sentiment,
		...(ratedMessageId ? { ratedMessageId } : {}),
	}
}

/** The assistant reply a rating row is about, when it was stored with one. */
export function aiChatFeedbackTarget(message: {
	enrichments?: unknown
}): string | null {
	const { enrichments } = message
	if (typeof enrichments !== "object" || enrichments === null) return null
	const target = (enrichments as { ratedMessageId?: unknown }).ratedMessageId
	return typeof target === "string" ? target : null
}

/**
 * Whether a message is a thumbs rating rather than something the customer said.
 *
 * A rating is stored as a USER row so it shows in the transcript, which makes
 * every rated conversation look like the customer took the last turn.
 */
export function isAiChatFeedbackMessage(message: {
	content: string
	enrichments?: unknown
}): boolean {
	const { enrichments } = message
	if (
		typeof enrichments === "object" &&
		enrichments !== null &&
		(enrichments as { kind?: unknown }).kind ===
			AI_CHAT_FEEDBACK_ENRICHMENT_KIND
	) {
		return true
	}

	const content = message.content.trimStart()
	return AI_CHAT_FEEDBACK_PREFIXES.some((prefix) => content.startsWith(prefix))
}

/** The rating a message carries, from its marker or, on older rows, its sentence. */
export function aiChatFeedbackSentimentOf(message: {
	content: string
	enrichments?: unknown
}): AiChatFeedbackSentiment | null {
	const { enrichments } = message
	if (
		typeof enrichments === "object" &&
		enrichments !== null &&
		(enrichments as { kind?: unknown }).kind ===
			AI_CHAT_FEEDBACK_ENRICHMENT_KIND
	) {
		const sentiment = (enrichments as { sentiment?: unknown }).sentiment
		return sentiment === "positive" || sentiment === "negative"
			? sentiment
			: null
	}

	const content = message.content.trimStart()
	if (content.startsWith(AI_CHAT_FEEDBACK_PREFIXES[1])) return "negative"
	if (content.startsWith(AI_CHAT_FEEDBACK_PREFIXES[0])) return "positive"
	return null
}

/**
 * Rating rows taken out of a transcript (oldest first) and folded onto the
 * replies they rate, newest rating winning. A row from before ratings named
 * their reply belongs to the last reply above it.
 */
export function foldAiChatRatings<
	T extends {
		id: string
		role: string
		content: string
		enrichments?: unknown
	},
>(
	messages: T[],
): {
	messages: T[]
	ratings: Map<string, AiChatFeedbackSentiment>
} {
	const kept: T[] = []
	const ratings = new Map<string, AiChatFeedbackSentiment>()
	let lastReplyId: string | null = null
	for (const message of messages) {
		if (message.role === "USER" && isAiChatFeedbackMessage(message)) {
			const target = aiChatFeedbackTarget(message) ?? lastReplyId
			const sentiment = aiChatFeedbackSentimentOf(message)
			if (target && sentiment) ratings.set(target, sentiment)
			continue
		}
		if (message.role === "ASSISTANT") lastReplyId = message.id
		kept.push(message)
	}
	return { messages: kept, ratings }
}

// A dislike is only worth a thread when it says what went wrong, so the modal
// holds Send until the text reads like a sentence rather than "bad".
export const COPILOT_FEEDBACK_MIN_LENGTH = 40
export const COPILOT_FEEDBACK_MIN_WORDS = 6

export function isCopilotFeedbackLongEnough(text: string): boolean {
	const trimmed = text.trim()
	if (trimmed.length < COPILOT_FEEDBACK_MIN_LENGTH) return false
	return trimmed.split(/\s+/).length >= COPILOT_FEEDBACK_MIN_WORDS
}

export function readAiChatFeedbackSentiment(
	raw: unknown,
): AiChatFeedbackSentiment | null {
	if (!raw || typeof raw !== "object") return null
	const stored = (raw as { feedback?: unknown }).feedback
	if (!stored || typeof stored !== "object") return null
	const sentiment = (stored as { sentiment?: unknown }).sentiment
	return sentiment === "positive" || sentiment === "negative" ? sentiment : null
}
